import React, { useState, useEffect } from 'react';
import { View, ScrollView } from 'react-native';
import { connect } from 'react-redux';

import { scale } from '@components/frame';
import { getSecureValue, setSecureValue, isWeb, Popup } from '@helpers';

import ColorWrapper from '@styles/colors';

import Profile from './components/profile';
import NewAccount from './components/newAccount';
import EditAccount from './components/editAccount';
import EditField from '@components/common/editField';
import Header from '@components/settings/v2/common/SettingsHeader';

import { selectNewAccount } from '@reducers/user';
import { addWebAccount, switchWebAccount } from '@reducers/user/webMultiAccount';
import { msalInstance } from '@config/msalConfig';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { font } from '@styles/fonts';
import { SCREEN_PADDING_HORIZONTAL } from '@v2/pages/common/constants/base.constants';

const AccountDetailsComponent = (data) => (props) => {
  const {
    C,
    user: { userInfo = {} },
    selectNewAccount,
  } = props;
  const dispatch = useAppDispatch();
  const [showEditField, setShowEditField] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [editFieldConfig, setEditFieldConfig] = useState({});

  const toggleShowNewAccount = () => {
    if (isWeb) {
      dispatch(addWebAccount());
      return;
    }
    Popup({
      Component: (p) => (
        <NewAccount
          {...{
            ...p,
            setAccounts,
            accounts,
            selectNewUser,
            ...data,
          }}
        />
      ),
    });
  };

  const toggleEditField = () => {
    setShowEditField(!showEditField);
  };

  const [accounts, setAccounts] = useState(false);

  const selectNewUser = async (data) => {
    if (isWeb && data?.homeAccountId) {
      setAccountLoading(data.userInfo?.emailAddress || data.homeAccountId);
      await dispatch(switchWebAccount(data.homeAccountId));
      setAccountLoading(false);
      return;
    }
    if (data) {
      setAccountLoading(data.userInfo.emailAddress);
    }
    setTimeout(async () => {
      await selectNewAccount(data);
      setAccountLoading(false);
    }, 10);
  };

  useEffect(() => {
    if (isWeb) {
      // On web, derive accounts from MSAL cache
      const msalAccounts = msalInstance.getAllAccounts();
      const mapped = msalAccounts.map((a) => ({
        userInfo: { emailAddress: a.username, companyProfile: { companyName: '' } },
        homeAccountId: a.homeAccountId,
      }));
      setAccounts(mapped.length > 0 ? mapped : false);
      return;
    }

    (async () => {
      const acc = getSecureValue('accounts');
      if (acc) {
        setAccounts(JSON.parse(acc));
      } else {
        const token = getSecureValue('token');
        const expiresIn = getSecureValue('expiresIn');
        const refreshToken = getSecureValue('refreshToken');
        const deviceCode = getSecureValue('deviceCode');
        const env = getSecureValue('env');
        const role = getSecureValue('role');
        const email = getSecureValue('email');

        const currentAccount = [
          {
            userInfo,
            token,
            expiresIn,
            refreshToken,
            deviceCode,
            env,
            role,
            email,
          },
        ];

        setSecureValue('accounts', JSON.stringify(currentAccount));
        setAccounts(currentAccount);
      }
    })();
  }, []);

  useEffect(() => {
    if (isWeb) {
      // On web, refresh MSAL accounts when userInfo changes
      const msalAccounts = msalInstance.getAllAccounts();
      const mapped = msalAccounts.map((a) => ({
        userInfo: { emailAddress: a.username, companyProfile: { companyName: '' } },
        homeAccountId: a.homeAccountId,
      }));
      if (mapped.length > 0) setAccounts(mapped);
      return;
    }

    (async () => {
      if (!accounts || !userInfo?.emailAddress) {
        return;
      }
      const exist = accounts?.find((u) => u.userInfo.emailAddress === userInfo.emailAddress);
      if (!exist) {
        const acc = getSecureValue('accounts');
        if (acc) {
          setAccounts(JSON.parse(acc));
        }
      }
    })();
  }, [userInfo]);

  return [
    <View style={{ width: '100%', height: '100%', backgroundColor: C('Background/Light/Detail'), alignItems: 'center' }} key="AccountDetails">
      <Header
        {...{
          backBtnText: 'Settings',
          name: 'AccountDetails',
          actionConfig: {
            type: 'Light Text Link',
            variant: 'S',
            color: C('Link/Light/Primary'),
            textTransform: 'none',
            ...font['Body'],
          },
          action: () => {
            Popup({
              Component: (p) => (
                <EditAccount
                  {...{
                    ...p,
                    setEditFieldConfig,
                    toggleEditField,
                  }}
                />
              ),
            });
          },
        }}
      />
      <ScrollView style={{ width: '100%', flex: 1 }} contentContainerStyle={{ paddingBottom: scale(50) }}>
        <View
          style={{
            paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL),
          }}
        >
          <Profile
            {...{ ...props, setEditFieldConfig, toggleEditField, toggleShowNewAccount, accounts, setAccounts, selectNewUser, accountLoading }}
          />
        </View>
      </ScrollView>
    </View>,
    <EditField
      key="EditField"
      {...{
        C,
        showModal: showEditField,
        onClose: toggleEditField,
        ...editFieldConfig,
      }}
    />,
  ];
};

export default (data) => connect(({ user }) => ({ user }), { selectNewAccount })(ColorWrapper(AccountDetailsComponent(data)));
